import React from 'react';
import { DarkModeContext } from './context/DarkModeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import PublicForm from './pages/PublicForm';
import PublicBuyPage from './pages/PublicBuyPage';
import CheckoutResult from './pages/CheckoutResult';
import PublicFunnel, { parseFunnelPath } from './pages/PublicFunnel';
import PublicLegalPage from './pages/PublicLegalPage';
import PublicSecureCX from './pages/PublicSecureCX';
import Shell from './components/Shell';
import './App.css';
import './styles/phase2.css';
import './styles/phase3.css';
import './styles/leads.css';
import './styles/funnels.css';
import './styles/tasks.css';
import './styles/securecx.css';

// ── Public route detection — runs at module load BEFORE React mounts ────────
// This guarantees public pages (funnel / buy / form) never inherit the
// dashboard's body scroll-lock, even for a single frame.
if (typeof document !== 'undefined') {
  const _publicPath = window.location.pathname;
  if (/^\/(?:funnel|buy|f|checkout|secure)\//i.test(_publicPath)) {
    document.body.classList.add('pf-body');
  }
}

export const ModuleContext = React.createContext({ currentModule: 'dashboard', setCurrentModule: () => {} });
export const useModule = () => React.useContext(ModuleContext);

const Bootstrapped = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="boot-splash" data-testid="boot-splash">Loading LegacyCRM…</div>;
  if (!user) return <Login />;
  return <DataProvider><Shell /></DataProvider>;
};

// Root-domain website gate — if a Website has been imported & published, the
// app's root path ('/') shows it instead of the CRM login, but ONLY when
// accessed via a real custom domain (once linked in Deploy -> Link Domain).
// On Emergent's own preview/deployed URLs, root always shows the CRM so
// hitting "Preview" during development never gets redirected away.
// crm.legacytriangle.com is also treated as CRM-managed so staff always land
// on the CRM login there instead of being redirected to a public website.
const CRM_HOSTNAMES = ['crm.legacytriangle.com'];
const isEmergentManagedHost = (hostname) => (
  hostname.endsWith('.emergentagent.com')
  || hostname.endsWith('.emergent.host')
  || hostname === 'localhost'
  || hostname === '127.0.0.1'
  || CRM_HOSTNAMES.includes(hostname)
);

// Path-based site slugs — a fallback for when a dedicated subdomain
// (ltcap.legacytriangle.com) can't get SSL coverage on the hosting platform.
// Visiting legacytriangle.com/ltcap resolves the same way a subdomain would,
// with zero extra DNS: the top-level path segment matches a website's
// custom_domain prefix (e.g. custom_domain "ltcap.legacytriangle.com" matches
// slug "ltcap") and renders that site, same as the hostname lookup. An
// OPTIONAL second segment (e.g. /ltcap/about) is a specific page within that
// site — it maps 1:1 to "{page}.html" in the site's own folder, so every page
// of a multi-page static site also gets its own clean, shareable URL.
// "login", "f", "buy", "funnel", "checkout", "reset-password" are reserved
// app routes and are never treated as a website slug.
const RESERVED_SLUGS = new Set(['login', 'f', 'buy', 'funnel', 'checkout', 'reset-password', 'privacy-policy', 'terms-of-service', 'about-us', 'contact-us']);
const getSiteRoute = () => {
  const m = window.location.pathname.match(/^\/([a-z0-9-]+)(?:\/([a-z0-9-]+))?\/?$/i);
  if (!m) return { slug: null, page: null };
  const slug = m[1].toLowerCase();
  if (RESERVED_SLUGS.has(slug)) return { slug: null, page: null };
  return { slug, page: m[2] ? m[2].toLowerCase() : null };
};

const RootGate = ({ darkMode, toggleDark, currentModule, setCurrentModule }) => {
  const { slug, page } = React.useMemo(getSiteRoute, []);
  const isRoot = window.location.pathname === '/';
  const [state, setState] = React.useState(
    (isEmergentManagedHost(window.location.hostname) && !slug) ? 'none' : 'checking'
  ); // checking | none | site
  const [siteId, setSiteId] = React.useState(null);
  React.useEffect(() => {
    if (state !== 'checking') return;
    const qs = slug ? `slug=${encodeURIComponent(slug)}` : `host=${encodeURIComponent(window.location.hostname)}`;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/websites/main?${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((site) => {
        if (site && site.is_published) {
          // Both root-domain visits (e.g. www.legacytriangle.com/) AND
          // path-based slug visits (e.g. legacytriangle.com/ltcap or
          // /ltcap/about) render the published site inline via iframe so the
          // address bar stays on the clean URL — every site (and every page
          // of a multi-page site) keeps its own slug instead of collapsing
          // into the long /api/public/websites/{id}/files/ path (same
          // technique as PublicLegalPage for /privacy-policy etc).
          setSiteId(site.id);
          setState('site');
        } else {
          setState('none');
        }
      })
      .catch(() => setState('none'));
  }, [state, slug, isRoot]);
  if (state === 'checking') return <div className="boot-splash" data-testid="boot-splash">Loading LegacyCRM…</div>;
  if (state === 'site') {
    return (
      <iframe
        title="Legacy Triangle"
        src={`/api/public/websites/${siteId}/files/${page ? `${page}.html` : ''}`}
        data-testid={slug ? `site-iframe-${slug}${page ? `-${page}` : ''}` : 'hub-homepage-iframe'}
        style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', border: 'none' }}
      />
    );
  }
  // AuthProvider (and its automatic /auth/me check) only mounts once we've
  // confirmed this visit should actually show the CRM — never during the
  // custom-domain website lookup above. Mounting it earlier caused a 401 from
  // /auth/me to force-redirect to /login (via the axios interceptor) before
  // the website lookup fetch could finish, hijacking custom-domain visitors
  // away from their published website and onto the CRM login screen.
  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDark }}>
      <AuthProvider>
        <ModuleContext.Provider value={{ currentModule, setCurrentModule }}>
          <Bootstrapped />
        </ModuleContext.Provider>
      </AuthProvider>
    </DarkModeContext.Provider>
  );
};

function App() {
  // Public form route — bypass auth and Bootstrapped entirely
  const publicMatch = React.useMemo(() => {
    const m = window.location.pathname.match(/^\/f\/([a-z0-9-]+)\/?$/i);
    return m ? m[1] : null;
  }, []);

  // Public commerce buy-page route
  const buyMatch = React.useMemo(() => {
    const m = window.location.pathname.match(/^\/buy\/([a-z0-9-]+)\/?$/i);
    return m ? m[1] : null;
  }, []);

  // Public SecureCX document-collection route — /secure/{token}
  const secureMatch = React.useMemo(() => {
    const m = window.location.pathname.match(/^\/secure\/([a-zA-Z0-9_-]+)\/?$/);
    return m ? m[1] : null;
  }, []);

  // Dedicated checkout success/cancel pages — always reached on this app's
  // own domain regardless of where checkout was initiated from.
  const checkoutMode = React.useMemo(() => {
    if (window.location.pathname === '/checkout/success') return 'success';
    if (window.location.pathname === '/checkout/cancel') return 'cancel';
    return null;
  }, []);

  // Password-reset landing page (link from the "forgot password" email)
  const isResetPasswordPath = window.location.pathname === '/reset-password';

  // Public funnel render route — /funnel/{slug} or /funnel/{slug}/{step}
  const funnelMatch = React.useMemo(() => parseFunnelPath(window.location.pathname), []);

  const [currentModule, setCurrentModule] = React.useState('dashboard');
  const [darkMode, setDarkMode] = React.useState(() => {
    try { return localStorage.getItem('legacycrm.darkMode') === '1'; } catch { return false; }
  });
  const toggleDark = () => setDarkMode((d) => !d);

  React.useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
    try { localStorage.setItem('legacycrm.darkMode', darkMode ? '1' : '0'); } catch {}
  }, [darkMode]);

  // Clean-URL standalone pages — legacytriangle.com/privacy-policy,
  // /terms-of-service, /about-us, /contact-us all render the primary
  // website's standalone HTML page (see PublicLegalPage) instead of the
  // long /api/public/websites/{id}/files/... path.
  const legalMatch = React.useMemo(() => {
    const cleanUrlPages = new Set(['privacy-policy', 'terms-of-service', 'about-us', 'contact-us']);
    const p = window.location.pathname.replace(/^\//, '');
    return cleanUrlPages.has(p) ? p : null;
  }, []);

  if (publicMatch) {
    return <PublicForm slug={publicMatch} />;
  }

  if (legalMatch) {
    return <PublicLegalPage slug={legalMatch} />;
  }

  if (buyMatch) {
    return <PublicBuyPage slug={buyMatch} />;
  }

  if (secureMatch) {
    return <PublicSecureCX token={secureMatch} />;
  }

  if (checkoutMode) {
    return <CheckoutResult mode={checkoutMode} />;
  }

  if (isResetPasswordPath) {
    const token = new URLSearchParams(window.location.search).get('token');
    return <ResetPassword token={token} />;
  }

  if (funnelMatch) {
    return <PublicFunnel slug={funnelMatch.slug} initialStep={funnelMatch.step} />;
  }

  const isLoginPath = window.location.pathname === '/login';

  if (isLoginPath) {
    return (
      <DarkModeContext.Provider value={{ darkMode, toggleDark }}>
        <AuthProvider>
          <ModuleContext.Provider value={{ currentModule, setCurrentModule }}>
            <Bootstrapped />
          </ModuleContext.Provider>
        </AuthProvider>
      </DarkModeContext.Provider>
    );
  }

  return (
    <RootGate
      darkMode={darkMode}
      toggleDark={toggleDark}
      currentModule={currentModule}
      setCurrentModule={setCurrentModule}
    />
  );
}

export default App;
